import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { FoundingMember, FounderPlan } from '@/types/founder';
import { FOUNDER_CONFIG, getFounderGraceDaysRemaining } from '@/types/founder';

interface UseFounderStatusReturn {
  founder: FoundingMember | null;
  isLoading: boolean;
  isFounder: boolean;
  isActiveFounder: boolean;
  isExpiredFounder: boolean;
  founderCurrentPlan: FounderPlan | null;
  isFounderOnLegacy: boolean;
  canUpgradeToLegacy: boolean;
  canDowngradeToConnection: boolean;
  graceDaysRemaining: number | null;
  shouldShowReminder: boolean;
  slotsRemaining: number | null;
  dismissReminder: () => void;
  upgradeToLegacy: () => Promise<boolean>;
  downgradeToConnection: () => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useFounderStatus(): UseFounderStatusReturn {
  const { user, isHydrated } = useAuth();
  const [founder, setFounder] = useState<FoundingMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [slotsRemaining, setSlotsRemaining] = useState<number | null>(null);

  const fetchSlotsRemaining = useCallback(async () => {
    try {
      const { data, error: rpcError } = await (supabase as any).rpc('get_founder_slots_used');
      if (!rpcError && typeof data === 'number') {
        setSlotsRemaining(FOUNDER_CONFIG.maxSlots - data);
      }
    } catch (err) {
      console.error('Error fetching founder slots:', err);
    }
  }, []);


  const fetchFounderStatus = useCallback(async () => {
    if (!isHydrated) return;

    if (!user) {
      setFounder(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('founding_members')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setFounder(data as unknown as FoundingMember | null);
    } catch (err) {
      console.error('Error fetching founder status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isHydrated, user?.id]);

  useEffect(() => {
    fetchSlotsRemaining();
  }, [fetchSlotsRemaining]);

  useEffect(() => {
    fetchFounderStatus();
  }, [fetchFounderStatus]);

  const isFounder = !!founder;
  const isActiveFounder = !!founder && founder.founder_status === 'active';
  const isExpiredFounder = !!founder && founder.founder_status === 'expired';
  const founderCurrentPlan: FounderPlan | null = founder?.current_plan ?? null;
  const isFounderOnLegacy = isActiveFounder && founderCurrentPlan === 'legacy';
  const canUpgradeToLegacy = isActiveFounder && founderCurrentPlan === 'connection';
  const canDowngradeToConnection = isActiveFounder && founderCurrentPlan === 'legacy';

  const graceDaysRemaining = founder
    ? getFounderGraceDaysRemaining(founder.last_out_of_announcements_date)
    : null;

  const shouldShowReminder = (() => {
    if (!founder || founder.founder_status !== 'active') return false;
    if (graceDaysRemaining === null || graceDaysRemaining > 7) return false;
    if (founder.reminder_dismissed_at) {
      const dismissedAt = new Date(founder.reminder_dismissed_at);
      const hoursSinceDismiss = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceDismiss < FOUNDER_CONFIG.dismissDurationHours) return false;
    }
    return true;
  })();

  const dismissReminder = useCallback(async () => {
    if (!user || !founder) return;
    const now = new Date().toISOString();

    await supabase
      .from('founding_members')
      .update({ reminder_dismissed_at: now })
      .eq('user_id', user.id);

    setFounder((prev) => (prev ? { ...prev, reminder_dismissed_at: now } : null));
  }, [user?.id, founder]);

  const upgradeToLegacy = useCallback(async (): Promise<boolean> => {
    if (!user || !founder || !canUpgradeToLegacy) return false;

    try {
      const { data, error } = await supabase.rpc('set_founder_plan', { p_plan: 'legacy' });
      if (error) throw error;
      const result = (data as { success?: boolean } | null) || {};
      if (!result.success) return false;

      const now = new Date().toISOString();
      setFounder((prev) =>
        prev ? { ...prev, current_plan: 'legacy', legacy_upgraded_at: now } : null
      );
      return true;
    } catch (err) {
      console.error('Error upgrading to Legacy:', err);
      return false;
    }
  }, [user?.id, founder, canUpgradeToLegacy]);

  const downgradeToConnection = useCallback(async (): Promise<boolean> => {
    if (!user || !founder || !canDowngradeToConnection) return false;

    try {
      const { data, error } = await supabase.rpc('set_founder_plan', { p_plan: 'connection' });
      if (error) throw error;
      const result = (data as { success?: boolean } | null) || {};
      if (!result.success) return false;

      const now = new Date().toISOString();
      setFounder((prev) =>
        prev ? { ...prev, current_plan: 'connection', legacy_downgraded_at: now } : null
      );
      return true;
    } catch (err) {
      console.error('Error downgrading to Connection:', err);
      return false;
    }
  }, [user?.id, founder, canDowngradeToConnection]);

  return {
    founder,
    isLoading,
    isFounder,
    isActiveFounder,
    isExpiredFounder,
    founderCurrentPlan,
    isFounderOnLegacy,
    canUpgradeToLegacy,
    canDowngradeToConnection,
    graceDaysRemaining,
    shouldShowReminder,
    slotsRemaining,
    dismissReminder,
    upgradeToLegacy,
    downgradeToConnection,
    refetch: fetchFounderStatus,
  };
}
